import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useTheme } from '../contexts/ThemeContext';

const { FiBookOpen, FiUsers, FiTrendingUp } = FiIcons;

const Header = ({ completedCount, totalCount, progressPercentage }) => {
  const { isDarkMode } = useTheme();

  const getProgressColor = () => {
    if (progressPercentage === 100) return 'bg-gradient-to-r from-green-500 to-green-600';
    if (progressPercentage >= 75) return 'bg-gradient-to-r from-blue-500 to-blue-600';
    if (progressPercentage >= 50) return 'bg-gradient-to-r from-yellow-500 to-orange-500';
    return 'bg-gradient-to-r from-red-500 to-red-600';
  };

  return (
    <motion.div 
      className={`rounded-custom p-6 shadow-custom-xl mb-6 border transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
      }`}
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="text-center mb-6">
        <motion.h1 
          className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-primary-600 to-purple-600 bg-clip-text text-transparent mb-2"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          🎓 Videocorso Formativo
        </motion.h1>
        <motion.p 
          className={`text-lg transition-colors duration-300 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-500'
          }`}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          Percorso con accesso progressivo per cartelle e sottocartelle
        </motion.p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <motion.div 
          className={`rounded-custom p-5 text-center border hover:shadow-custom-md transition-all duration-300 ${
            isDarkMode 
              ? 'bg-gradient-to-br from-gray-700 to-gray-800 border-gray-600' 
              : 'bg-gradient-to-br from-gray-100 to-white border-gray-200'
          }`}
          whileHover={{ y: -2, scale: 1.02 }}
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          <div className="flex items-center justify-center mb-2">
            <SafeIcon icon={FiBookOpen} className="text-2xl text-primary-600 mr-2" />
            <span className="text-3xl font-bold text-primary-600">{completedCount}</span>
          </div>
          <div className={`text-sm font-medium transition-colors duration-300 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-500'
          }`}>
            Lezioni completate
          </div>
        </motion.div>

        <motion.div 
          className={`rounded-custom p-5 text-center border hover:shadow-custom-md transition-all duration-300 ${
            isDarkMode 
              ? 'bg-gradient-to-br from-gray-700 to-gray-800 border-gray-600' 
              : 'bg-gradient-to-br from-gray-100 to-white border-gray-200'
          }`}
          whileHover={{ y: -2, scale: 1.02 }}
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <div className="flex items-center justify-center mb-2">
            <SafeIcon icon={FiUsers} className="text-2xl text-primary-600 mr-2" />
            <span className="text-3xl font-bold text-primary-600">{totalCount}</span>
          </div>
          <div className={`text-sm font-medium transition-colors duration-300 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-500'
          }`}>
            Lezioni totali
          </div>
        </motion.div>

        <motion.div 
          className={`rounded-custom p-5 text-center border hover:shadow-custom-md transition-all duration-300 ${
            isDarkMode 
              ? 'bg-gradient-to-br from-gray-700 to-gray-800 border-gray-600' 
              : 'bg-gradient-to-br from-gray-100 to-white border-gray-200'
          }`}
          whileHover={{ y: -2, scale: 1.02 }}
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
        >
          <div className="flex items-center justify-center mb-2">
            <SafeIcon icon={FiTrendingUp} className="text-2xl text-primary-600 mr-2" />
            <span className="text-3xl font-bold text-primary-600">{progressPercentage}%</span>
          </div>
          <div className={`text-sm font-medium transition-colors duration-300 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-500'
          }`}>
            Avanzamento
          </div>
        </motion.div>
      </div>

      <motion.div 
        className={`relative h-6 rounded-custom overflow-hidden shadow-inner transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
        }`}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.8 }}
      >
        <motion.div 
          className={`h-full ${getProgressColor()} flex items-center justify-center text-white text-sm font-semibold relative overflow-hidden`}
          initial={{ width: 0 }}
          animate={{ width: `${progressPercentage}%` }}
          transition={{ duration: 1.5, ease: "easeOut", delay: 1 }}
        >
          <span className="relative z-10">{progressPercentage}%</span>
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer"></div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default Header;